import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card } from 'reactstrap';
import { Input, ISelectFieldValue, ISelectOption } from 'react-ocean-forms-bootstrap';
import { ComponentAsyncSelect, PCCreatableSelect } from '../Forms';
import { ParameterForm } from './ParameterForm.types';
import { UnitQuery, MaterialQuery, DimensionQuery, NonMaterialQuery, SamplePretreatmentQuery } from '../../graphql/Components';
import { ActionMeta } from 'react-select/lib/types';
import { validators, useFormContext } from 'react-ocean-forms';
import { ViewMode } from '../Common/ViewMode';
import { useParameterContext } from './ParameterProvider';
import { Gate } from '@oceanjs/core';
import { useRole } from '../../hooks';
import { Role } from '../../Roles';
import { Einheit, KomponenteStatus, ParameterStatus } from '../../generated/types';
import { KomponentenTypEnum } from '../ParamComponents';


/**
 * @author Jungwirth
 */
export const ParameterMandatoryForm: React.FC<ParameterForm> = ({ viewConfig }: Readonly<ParameterForm>): JSX.Element => {
  const form = useFormContext();
  const [dimValue, setDimValue] = useState<string | undefined>(undefined);
  const [samplevalue, setSampleValue] = useState<string | undefined>(undefined);
  const [materialVisible, setMaterialVisible] = useState<boolean | undefined>(undefined);
  const { resetForm } = useParameterContext();
  const  role = useRole();


  const status = (form.getValues()['status'] as ISelectOption)?.value ?? undefined;
  const disabledMand = (ParameterStatus.Ps2A === status || ParameterStatus.Ps2B === status) && viewConfig.viewName === ViewMode.Edit;

  const kompStatusList: KomponenteStatus[] = viewConfig.viewName === ViewMode.New || viewConfig.viewName === ViewMode.Edit 
    ? [KomponenteStatus.Ks1, KomponenteStatus.Ks2A] 
    : [];

  
  useEffect(() => {
    const dim = form.defaultValues['dimension'] as ISelectOption;
    if(dim) setDimValue(dim.label);
  },[form]);

  // reset field info on form reset
  useEffect(() => {
    setMaterialVisible(undefined);
    setDimValue(undefined);
  },[resetForm]);

  // handle material and non material XOR
  useEffect(() => {
    if(form.defaultValues.material) {
      setMaterialVisible(true);
    }
    if(form.defaultValues.non_material) {
      setMaterialVisible(false);
    }
  },[form.defaultValues]);

  /**
   * handle changes on unit selection and update dimension value 
   */
  const handleUnitChange = useCallback((value: ISelectFieldValue, action?: ActionMeta) => {
    // console.log('handle unit change ', value, action);
    if(action?.action === 'select-option') {
      if(!Array.isArray(value)) {
        const einheit: Einheit = JSON.parse(value.value);
        setDimValue(einheit?.dimension.name);
      }
    }
    if(action?.action === 'clear') {
      setDimValue(undefined);
    }
    
  },[]);

  /**
   * handle changes on material or non material select and update disabled settings 
   */
  const handleMaterialNonMaterial = useCallback((type: KomponentenTypEnum, action?: ActionMeta) => {
    if(action?.action === 'select-option' && type === KomponentenTypEnum.Stoff) {
      setMaterialVisible(true);
      setSampleValue(undefined);
    }
    if(action?.action === 'select-option' && type === KomponentenTypEnum.Nichtstofflich) {
      setMaterialVisible(false);
      // set sample pretreatment 
      setSampleValue('keine Probenvorbehandlung');

    }
    if(action?.action === 'clear') {
      setMaterialVisible(undefined);
      setSampleValue(undefined);
    }

  },[]);
  
  return (
    <Card body className="mb-2">
      <Row>
        <Col md={6}>
          <Gate handle={role === Role.EHPC_RW || status === undefined || status === ParameterStatus.Ps2A || status === ParameterStatus.Ps2B || status === ParameterStatus.Ps3}>
            <Input
              name="code"
              label="parametercode"
              {...viewConfig.fields.parametercode.field}
            />
          </Gate>
          <Gate handle={status === ParameterStatus.Ps1 && role !== Role.EHPC_RW }>
            <Input
                name="codeDraft"
                label="parametercode"
                {...viewConfig.fields.parametercode.field}
              />
          </Gate>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Input
            name="name"
            label="parametername"
            {...viewConfig.fields.parametername.field}
            disabled={viewConfig.fields.parametername.field.disabled}
          />
        </Col>
        <Col md={6}>
        <Gate handle={!viewConfig.fields.alternativname.field.hidden}>
            <PCCreatableSelect
              name="alternativnamen"
              label="alternate_names"
              createLabel="new_alternate_name"
              {...viewConfig.fields.alternativname.field}
              disabled={viewConfig.fields.alternativname.field.disabled || disabledMand}
              
            />
          </Gate>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ComponentAsyncSelect
            name="dimension"
            searchValue={dimValue}
            query={DimensionQuery.getPagedComponents()}
            path="dimensionen"
            status={kompStatusList}
            getLabel={DimensionQuery.getDropdownLabel}
            fieldConfig={
              {...viewConfig.fields.dimension.field,
              disabled: viewConfig.fields.dimension.field.disabled || disabledMand,
              }
              }
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ComponentAsyncSelect
            name="material"
            query={MaterialQuery.getPagedComponents()}
            path="stoffe"
            status={kompStatusList}
            handleChange={(value: ISelectFieldValue, action?: ActionMeta) => handleMaterialNonMaterial(KomponentenTypEnum.Stoff, action)}
            getLabel={MaterialQuery.getDropdownLabel}
            fieldConfig={
              {...viewConfig.fields.material.field, 
              disabled: viewConfig.fields.material.field.disabled || materialVisible === false || disabledMand,
              validators: materialVisible === false || viewConfig.viewName === ViewMode.Search ? [] : [validators.required]
              }
              }
          />
        </Col>
        <Col md={6}>
          <ComponentAsyncSelect
            name="non_material"
            query={NonMaterialQuery.getPagedComponents()}
            status={kompStatusList}
            handleChange={(value: ISelectFieldValue, action?: ActionMeta) => handleMaterialNonMaterial(KomponentenTypEnum.Nichtstofflich, action)}
            fieldConfig={
              {...viewConfig.fields.non_material.field,
              disabled: viewConfig.fields.non_material.field.disabled || materialVisible === true || disabledMand,
              validators: materialVisible === true || viewConfig.viewName === ViewMode.Search ? [] : [validators.required]}}
            path="nichtstoffliche"/>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <ComponentAsyncSelect
            name="unit"
            query={UnitQuery.getPagedComponents()}
            status={kompStatusList}
            getLabel={UnitQuery.getDropdownLabel}
            fieldConfig={
              {...viewConfig.fields.unit.field,
              disabled: viewConfig.fields.unit.field.disabled || disabledMand,
              }
              }
            handleChange={handleUnitChange}
            path="einheiten"/>
        </Col>
        <Col md={6}>
          <ComponentAsyncSelect
            name="sample_pretreatment"
            query={SamplePretreatmentQuery.getPagedComponents()}
            searchValue={samplevalue}
            status={kompStatusList}
            fieldConfig={
              {...viewConfig.fields.sample_pretreatment.field,
              disabled: viewConfig.fields.sample_pretreatment.field.disabled || disabledMand,
              }
              }
            path="probenvorbehandlungen"/>
        </Col>
      </Row>
    </Card>
  );
};
